import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Dialog, Divider, List, Paragraph, Portal } from 'react-native-paper';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { scenarios } from '../../../scenarios';

export function AllScenariosDialog({ language, setVisible, visible }) {
  const hideDialog = () => setVisible(false);
  const { values } = useFormikContext();

  function capitalizeFirstLetter(text) {
    return text[0].toUpperCase() + text.slice(1);
  }

  function getPrefix(idx, language) {
    if (language === 'en') {
      return `S${idx + 1}`;
    } else if (language === 'es') {
      return `E${idx + 1}`;
    } else {
      return `S${idx + 1}`;
    }
  }

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={hideDialog}>
        <Dialog.Content>
          <ScrollView>
            {scenarios[language].map((scenario, idx) => (
              <View key={`${scenario.id}-view`}>
                <Text style={{ fontWeight: 'bold' }}>
                  {getPrefix(idx, language)} - {scenario.name}
                </Text>
                <Paragraph>{scenario.description}</Paragraph>
                {scenario.id === 'planting' || scenario.id === 'custom' ? (
                  <View>
                    <Text></Text>
                    <Text style={{ fontWeight: 'bold' }}>Selected values:</Text>
                    {scenario.id === 'planting'
                      ? values.crops.map((crop) => (
                          <Text key={crop.name}>
                            {capitalizeFirstLetter(crop.name)}:{' '}
                            {`${crop.plantDistribution}%`}
                            {'  '}
                          </Text>
                        ))
                      : scenario.id === 'custom'
                      ? values.crops.map((crop) => (
                          <Text key={crop.name}>
                            {capitalizeFirstLetter(crop.name)}:{' '}
                            {`${crop.bound}%`}
                            {'  '}
                          </Text>
                        ))
                      : null}
                  </View>
                ) : null}
                <Divider style={{ margin: 15 }} />
              </View>
            ))}
          </ScrollView>
        </Dialog.Content>
      </Dialog>
    </Portal>
  );
}

AllScenariosDialog.propTypes = {
  language: PropTypes.string,
  setVisible: PropTypes.func,
  visible: PropTypes.bool,
};

export function ScenarioDialog({
  displayUserInputs,
  scenario,
  setVisible,
  visible,
}) {
  const hideDialog = () => setVisible(false);
  const { values } = useFormikContext();

  function capitalizeFirstLetter(text) {
    return text[0].toUpperCase() + text.slice(1);
  }

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={hideDialog}>
        <Dialog.Title>{scenario ? scenario.name : null}</Dialog.Title>
        <Dialog.Content>
          <Paragraph>{scenario ? scenario.description : null}</Paragraph>
          {displayUserInputs && scenario ? (
            <Paragraph>
              {scenario.id === 'planting'
                ? values.crops.map((crop) => (
                    <List.Item
                      key={crop.name}
                      title={capitalizeFirstLetter(crop.name)}
                      description={`${crop.plantDistribution}%`}
                    />
                  ))
                : scenario.id === 'custom'
                ? values.crops.map((crop) => (
                    <List.Item
                      key={crop.name}
                      title={capitalizeFirstLetter(crop.name)}
                      description={`${crop.bound}%`}
                    />
                  ))
                : null}
            </Paragraph>
          ) : null}
        </Dialog.Content>
      </Dialog>
    </Portal>
  );
}

ScenarioDialog.propTypes = {
  displayUserInputs: PropTypes.bool,
  scenario: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  setVisible: PropTypes.func,
  visible: PropTypes.bool,
};

export function Scenario({ scenario, setScenario, showDialog }) {
  return (
    <View style={styles.linkContainer}>
      <Text
        style={styles.link}
        onPress={() => {
          setScenario(scenario);
          showDialog();
        }}
      >
        {scenario.title[0] + '\n' + scenario.title[1] + '\n'}
      </Text>
    </View>
  );
}

Scenario.propTypes = {
  scenario: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  setScenario: PropTypes.func,
  showDialog: PropTypes.func,
};

export default function Scenarios({ pageContent, toggleDisableNextStep }) {
  const [scenario, setScenario] = useState(undefined);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    toggleDisableNextStep(false);
  }, []);

  const showDialog = () => setVisible(true);

  return (
    <View style={styles.container}>
      <Text>{pageContent[0]}</Text>
      {pageContent.slice(1).map((scenario) => (
        <Scenario
          key={scenario.id}
          scenario={scenario}
          setScenario={setScenario}
          showDialog={showDialog}
        />
      ))}
      <ScenarioDialog
        scenario={scenario}
        setVisible={setVisible}
        visible={visible}
      />
    </View>
  );
}

Scenarios.propTypes = {
  pageContent: PropTypes.array,
  toggleDisableNextStep: PropTypes.func,
};

const styles = StyleSheet.create({
  button: { alignSelf: 'flex-start' },
  container: {
    flex: 1,
  },
  link: { color: '#555988', fontWeight: 'bold', textAlign: 'center' },
  linkContainer: { marginTop: 15 },
  textInput: {
    marginTop: 15,
  },
});
